import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../FoodCalculator/MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Convertisseur Grammes en Tasses - Conversion Précise pour la Cuisson";
const DESCRIPTION =
  "Convertisseur Grammes en Tasses - Conversion Précise pour la Cuisson pour adapter vos recettes, portions et conversions en cuisine. Resultat rapide et pratique pour cuisiner avec plus de precision chaque jour.";
const KEYWORDS =
  "convertisseur grammes en tasses conversion précise pour la cuisson, calculatrice convertisseur grammes en tasses conversion précise pour la cuisson, calcul en ligne, cuisine, recette, conversion, resultat rapide";
const OG_URL = "https://do-calculate.com/calculator/fr/grams-to-cups/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Convertisseur Grammes Tasses",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Convertisseur Grammes Tasses",
        item: OG_URL,
      },
    ],
  },
];

const INGREDIENT_DENSITIES = {
  flour: { name: "Farine (ordinaire)", gramsPerCup: 125 },
  breadFlour: { name: "Farine Pains", gramsPerCup: 130 },
  wholeWheatFlour: { name: "Farine Complète", gramsPerCup: 120 },
  sugar: { name: "Sucre Blanc", gramsPerCup: 200 },
  brownSugar: { name: "Sucre Brun (compacté)", gramsPerCup: 220 },
  powderedSugar: { name: "Sucre Poudre", gramsPerCup: 120 },
  butter: { name: "Beurre", gramsPerCup: 227 },
  milk: { name: "Lait Liquid", gramsPerCup: 240 },
  water: { name: "Eau", gramsPerCup: 240 },
  oil: { name: "Huile Végétale", gramsPerCup: 218 },
  honey: { name: "Miel", gramsPerCup: 340 },
  rice: { name: "Riz (Non Cuit)", gramsPerCup: 190 },
  oats: { name: "Flocons Avoine", gramsPerCup: 90 },
  cocoa: { name: "Cacao", gramsPerCup: 100 },
};

function GramsToCupsCalculator() {
  const [ingredient, setIngredient] = useState("flour");
  const [customDensity, setCustomDensity] = useState("");
  const [grams, setGrams] = useState("");
  const [cups, setCups] = useState("");
  const [unit, setUnit] = useState("grams");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateFromGrams = () => {
    try {
      if (!grams || parseFloat(grams) <= 0) {
        setError("");
        setResult(null);
        return;
      }

      const gramsValue = parseFloat(grams);
      let gramsPerCup;
      if (ingredient === "custom") {
        gramsPerCup = parseFloat(customDensity);
        if (!gramsPerCup || gramsPerCup <= 0) {
          setError("Veuillez entrer une densité valide (grammes/tasse)");
          setResult(null);
          return;
        }
      } else {
        gramsPerCup = INGREDIENT_DENSITIES[ingredient].gramsPerCup;
      }

      const cupsValue = gramsValue / gramsPerCup;
      setCups(cupsValue.toFixed(3));

      setResult({
        grams: gramsValue,
        cups: cupsValue,
        gramsPerCup: gramsPerCup,
        ingredientName: ingredient === "custom" ? "Ingrédient Personnalisé" : INGREDIENT_DENSITIES[ingredient].name,
        tablespoons: (cupsValue * 16).toFixed(1),
        teaspoons: (cupsValue * 48).toFixed(1),
        milliliters: (cupsValue * 240).toFixed(0),
      });
      setError("");
    } catch (err) {
      setError("Erreur Calcul");
      setResult(null);
    }
  };

  const calculateFromCups = () => {
    try {
      if (!cups || parseFloat(cups) <= 0) {
        setError("");
        setResult(null);
        return;
      }

      const cupsValue = parseFloat(cups);
      let gramsPerCup;
      if (ingredient === "custom") {
        gramsPerCup = parseFloat(customDensity);
        if (!gramsPerCup || gramsPerCup <= 0) {
          setError("Veuillez entrer une densité valide (grammes/tasse)");
          setResult(null);
          return;
        }
      } else {
        gramsPerCup = INGREDIENT_DENSITIES[ingredient].gramsPerCup;
      }

      const gramsValue = cupsValue * gramsPerCup;
      setGrams(gramsValue.toFixed(1));

      setResult({
        grams: gramsValue,
        cups: cupsValue,
        gramsPerCup: gramsPerCup,
        ingredientName: ingredient === "custom" ? "Ingrédient Personnalisé" : INGREDIENT_DENSITIES[ingredient].name,
        tablespoons: (cupsValue * 16).toFixed(1),
        teaspoons: (cupsValue * 48).toFixed(1),
        milliliters: (cupsValue * 240).toFixed(0),
      });
      setError("");
    } catch (err) {
      setError("Erreur Calcul");
      setResult(null);
    }
  };

  const handleGramsChange = (e) => {
    const value = e.target.value;
    setGrams(value);
    setUnit("grams");
    if (value) {
      calculateFromGrams();
    } else {
      setResult(null);
    }
  };

  const handleCupsChange = (e) => {
    const value = e.target.value;
    setCups(value);
    setUnit("cups");
    if (value) {
      calculateFromCups();
    } else {
      setResult(null);
    }
  };

  useEffect(() => {
    if (unit === "grams" && grams) {
      calculateFromGrams();
    } else if (unit === "cups" && cups) {
      calculateFromCups();
    }
  }, [ingredient, customDensity]);

  const handleClear = () => {
    setGrams("");
    setCups("");
    setUnit("grams");
    setResult(null);
    setError("");
    setCustomDensity("");
  };

  return (
    <div className="ltr" style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Convertisseur Grammes Tasses</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Convertisseur Grammes en Tasses</strong> est un outil intelligent pour convertir les mesures dans les recettes. 
                Nous avons tous un problème de conversion de grammes en tasses, surtout avec les différents ingrédients. 
                Ce convertisseur calcule pour vous la quantité avec précision selon le type d'ingrédient.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Convertir Entre Grammes et Tasses
                </h2>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="ingredient">Type Ingrédient</label>
                  <select
                    id="ingredient"
                    className="form-select"
                    value={ingredient}
                    onChange={(e) => setIngredient(e.target.value)}
                  >
                    {Object.entries(INGREDIENT_DENSITIES).map(([key, { name, gramsPerCup }]) => (
                      <option key={key} value={key}>
                        {name} ({gramsPerCup} g/tasse)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-sm-12 col-md-6">
                  <label>Grammes</label>
                  <input
                    type="number"
                    className="form-control"
                    value={grams}
                    onChange={handleGramsChange}
                    placeholder="Entrez grammes"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label>Tasses</label>
                  <input
                    type="number"
                    className="form-control"
                    value={cups}
                    onChange={handleCupsChange}
                    placeholder="Entrez tasses"
                  />
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ fontSize: "16px", padding: "10px 30px" }}>
                  Effacer
                </button>
              </div>

              {error && <div className="alert alert-warning">{error}</div>}

              {result && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>Résultats Conversion :</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>Grammes</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.grams.toFixed(1)} g</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Tasses</td>
                          <td style={{ textAlign: "center", fontWeight: "bold" }}>{result.cups.toFixed(3)}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Cuillerées À Soupe</td>
                          <td style={{ textAlign: "center" }}>{result.tablespoons}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Cuillerées À Thé</td>
                          <td style={{ textAlign: "center" }}>{result.teaspoons}</td>
                        </tr>
                        <tr>
                          <td style={{ textAlign: "center" }}>Millilitres</td>
                          <td style={{ textAlign: "center" }}>{result.milliliters}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Conseils d'utilisation</h3></div>
              <div className="info-section-body">
                <p>Pour obtenir un resultat pertinent, verifiez vos unites, saisissez des valeurs realistes et utilisez ce calcul comme aide a la decision, puis adaptez selon votre situation.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Convertir les grammes en tasses</h3>
              </div>
              <div className="info-section-body">
                <p>
                  La conversion grammes ↔ tasses est l'une des plus demandées en cuisine, surtout quand on suit
                  des recettes américaines. Une tasse (cup) correspond à 236,6 ml, mais son équivalent en grammes
                  varie selon l'ingrédient. Par exemple, une tasse de farine pèse environ 125 g alors qu'une tasse
                  de sucre en poudre en fait 200 g — soit 60 % de plus. Ce convertisseur applique la bonne densité
                  pour chaque ingrédient, ce qui garantit une conversion précise.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Grammes en tasses : valeurs de référence</h3>
              </div>
              <div className="info-section-body">
                <ul>
                  <li>Farine tout usage : 1 tasse = 125 g</li>
                  <li>Sucre blanc : 1 tasse = 200 g</li>
                  <li>Sucre glace : 1 tasse = 120 g</li>
                  <li>Beurre : 1 tasse = 227 g</li>
                  <li>Huile végétale : 1 tasse = 218 g</li>
                  <li>Lait entier : 1 tasse = 244 g</li>
                  <li>Eau : 1 tasse = 237 g</li>
                  <li>Riz cru : 1 tasse = 185 g</li>
                  <li>Flocons d'avoine : 1 tasse = 90 g</li>
                  <li>Cacao en poudre : 1 tasse = 100 g</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Comment peser la farine correctement</h3>
              </div>
              <div className="info-section-body">
                <p>
                  La farine est l'ingrédient le plus difficile à mesurer en volume. Si vous tassez la farine dans
                  la tasse, vous pouvez facilement dépasser 150 g au lieu de 125 g — ce qui rend les gâteaux secs
                  et compacts. La méthode correcte : versez la farine à la cuillère dans la tasse sans tasser, puis
                  arasez avec une spatule droite. Encore mieux : utilisez une balance de cuisine. Les boulangers
                  professionnels pèsent toujours leurs ingrédients pour garantir un résultat constant.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GramsToCupsCalculator;
